import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { NoticeCampaignListTable } from '@/components/venue/NoticeCampaignListTable'
import { useCampaigns } from '@/hooks/useCampaigns'
import { cn } from '@/lib/utils/cn'
import { todayDateKey } from '@/lib/venue/calendarGrid'
import type { VenueCampaignType } from '@/lib/venue/campaignTypes'
import {
  CAMPAIGN_TYPE_FILTER_OPTIONS,
  campaignRowPath,
  defaultCampaignListPeriod,
  validateCampaignPeriod,
  type CampaignListFilter,
} from '@/lib/venue/campaignViewModel'
import { useVenueContext } from '@/routes/venueContext'

type TypeFilter = VenueCampaignType | 'ALL'

function initialFilter(): CampaignListFilter {
  return defaultCampaignListPeriod(todayDateKey())
}

/**
 * 발송 이력 — 'notices/history'(설계 §2). 기간·유형 필터 + 목록(#714 §1-5, #728 §4).
 *
 * 🚨 이 목록은 작성일(`createdAt`) 축이고 임시저장·차단 건도 포함된다 — 홈의 요약(§3, `sentAt`+`SENT`만)과
 * 건수가 다를 수 있다(#728 §4 "두 화면 건수가 달라 보이는 것이 정상"). 사이드바에는 등록하지 않는다
 * (`sales`/`bookings/:reservationId`와 같은 관례 — 홈의 링크로만 진입, 설계 §자율 결정 7).
 */
export function VenueNoticeHistoryPage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()

  const [appliedFilter, setAppliedFilter] = useState<CampaignListFilter>(initialFilter)
  const [dateInputs, setDateInputs] = useState({ from: appliedFilter.from, to: appliedFilter.to })
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL')

  const listQuery = useCampaigns(venueId, appliedFilter)
  const periodError = validateCampaignPeriod(dateInputs)

  const pages = listQuery.data?.pages ?? []
  const items = pages.flatMap((page) => page.campaigns)

  function applyFilter(nextType: TypeFilter) {
    if (periodError) return
    setAppliedFilter({
      from: dateInputs.from,
      to: dateInputs.to,
      type: nextType === 'ALL' ? undefined : nextType,
    })
  }

  return (
    <>
      <PageHeader title="발송 이력" description="이 목록은 작성일 기준이며 임시저장·차단 건도 포함됩니다." />

      <p className="mb-4 text-xs text-slateish-600">
        관리 홈의 요약은 발송 완료(발송일 기준) 건만 집계하므로, 이 목록과 건수가 다를 수 있습니다.
      </p>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="notice-history-from">
            시작일
          </label>
          <Input
            id="notice-history-from"
            type="date"
            value={dateInputs.from}
            onChange={(event) => {
              setDateInputs((current) => ({ ...current, from: event.target.value }))
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="notice-history-to">
            종료일
          </label>
          <Input
            id="notice-history-to"
            type="date"
            value={dateInputs.to}
            onChange={(event) => {
              setDateInputs((current) => ({ ...current, to: event.target.value }))
            }}
          />
        </div>
        <Button size="md" disabled={Boolean(periodError)} onClick={() => applyFilter(typeFilter)}>
          조회
        </Button>
      </div>

      {periodError ? <p className="mb-3 text-xs text-danger-500">{periodError}</p> : null}

      <div role="group" aria-label="유형 필터" className="mb-4 flex flex-wrap gap-2">
        {(['ALL', ...CAMPAIGN_TYPE_FILTER_OPTIONS.map((option) => option.value)] as TypeFilter[]).map((value) => {
          const label = value === 'ALL' ? '전체' : CAMPAIGN_TYPE_FILTER_OPTIONS.find((o) => o.value === value)?.label
          const selected = typeFilter === value
          return (
            <button
              key={value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                setTypeFilter(value)
                applyFilter(value)
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              )}
            >
              {label}
            </button>
          )
        })}
      </div>

      {listQuery.isLoading ? <LoadingState rows={4} label="발송 이력을 불러오는 중" /> : null}

      {listQuery.isError ? (
        <ErrorState
          error={listQuery.error}
          onRetry={() => {
            void listQuery.refetch()
          }}
        />
      ) : null}

      {!listQuery.isLoading && !listQuery.isError ? (
        <NoticeCampaignListTable
          items={items}
          onSelect={(item) => {
            navigate(`/venues/${venueId}/${campaignRowPath(item)}`)
          }}
          hasNextPage={listQuery.hasNextPage ?? false}
          isFetchingNextPage={listQuery.isFetchingNextPage}
          onLoadMore={() => {
            void listQuery.fetchNextPage()
          }}
        />
      ) : null}
    </>
  )
}
